import type { SocialShareType } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { articleShareWidgetArticle$key } from "~/queries/__generated__/articleShareWidgetArticle.graphql";

import type { ContentShareWidgetProps } from "./styles";
import { Container, StyledArticleSpeechInlineWidget, StyledContentShareWidget } from "./styles";

type Variants =
  | "default"
  | "morningStudio"
  | "morningStudioSubheader"
  | "newsletter"
  | "generic"
  | "longRead"
  | "longReadLeft"
  | "longReadMobile"
  | "photoEssayLeft"
  | "photoEssay"
  | "photoEssayMobile"
  | "landingBanner"
  | "magazines-style";

type ButtonStyle = {
  backgroundColor?: string;
  color?: string;
  mobilePadding?: number;
  padding?: number;
  size?: number;
  svgTransform?: string;
};

type Options = {
  $props: ContentShareWidgetProps;
  buttonStyle?: ButtonStyle;
  hasArticleSpeech?: boolean;
  types: SocialShareType[];
};

const optionsMap: Record<Variants, Options> = {
  default: {
    $props: { $direction: "column" },
    buttonStyle: { size: 20 },
    types: ["facebook", "twitter", "whatsapp", "telegram", "copy-link", "email", "print"],
  },
  generic: {
    $props: { $direction: "column" },
    buttonStyle: { size: 20 },
    hasArticleSpeech: true,
    types: ["facebook", "twitter", "whatsapp", "telegram", "copy-link", "email", "print"],
  },
  landingBanner: {
    $props: { $direction: "row" },
    buttonStyle: { backgroundColor: "#ffffff", color: "#001246", size: 20 },
    types: ["facebook", "twitter", "copy-link", "email"],
  },
  longRead: {
    $props: { $direction: "row" },
    buttonStyle: { size: 20 },
    hasArticleSpeech: true,
    types: ["facebook", "twitter", "copy-link", "email"],
  },
  longReadLeft: {
    $props: { $direction: "column" },
    buttonStyle: { size: 20 },
    hasArticleSpeech: true,
    types: ["facebook", "twitter", "whatsapp", "telegram", "copy-link", "email", "print"],
  },
  longReadMobile: {
    $props: { $direction: "row" },
    buttonStyle: { size: 20 },
    types: ["facebook", "twitter"],
  },
  "magazines-style": {
    $props: { $direction: "row", $outlineColor: "#222222" },
    buttonStyle: { backgroundColor: "#ffffff", color: "#222222", size: 20 },
    types: ["facebook", "twitter", "copy-link", "email1"],
  },
  morningStudio: {
    $props: { $direction: "row" },
    buttonStyle: { size: 20 },
    types: ["facebook", "twitter", "copy-link", "email"],
  },
  morningStudioSubheader: {
    $props: { $direction: "row" },
    buttonStyle: { backgroundColor: "#909090", color: "#000000", size: 20 },
    types: ["facebook", "twitter", "copy-link", "email"],
  },
  newsletter: {
    $props: { $direction: "row" },
    buttonStyle: { size: 20 },
    types: ["facebook", "twitter", "copy-link", "email"],
  },
  photoEssay: {
    $props: { $direction: "row" },
    buttonStyle: { backgroundColor: "#ffffff", color: "#000000", size: 20 },
    types: ["facebook", "twitter", "copy-link", "email"],
  },
  photoEssayLeft: {
    $props: { $direction: "column" },
    buttonStyle: { backgroundColor: "#ffffff", color: "#000000", size: 20 },
    types: ["facebook", "twitter", "whatsapp", "telegram", "copy-link", "email", "print"],
  },
  photoEssayMobile: {
    $props: { $direction: "row" },
    buttonStyle: { backgroundColor: "#ffffff", color: "#000000", size: 20 },
    types: ["facebook", "twitter"],
  },
};

type Props = {
  className?: string;
  reference: articleShareWidgetArticle$key;
  variant?: Variants;
};

export const ArticleShareWidget: FunctionComponent<Props> = ({
  className,
  reference,
  variant = "default",
}) => {
  const content = useFragment<articleShareWidgetArticle$key>(
    graphql`
      fragment articleShareWidgetArticle on Article {
        ...contentShareWidgetContent
        ...articleCommentsTriggerArticle
        ...articleSpeechInlineWidgetArticle
      }
    `,
    reference,
  );

  const { $props, buttonStyle, hasArticleSpeech, types } = optionsMap[variant];

  const tooltipPlacement = $props.$direction === "column" ? "right" : "top";

  return (
    <Container className={className}>
      {hasArticleSpeech && (
        <StyledArticleSpeechInlineWidget reference={content} variant="generic-minify" />
      )}
      <StyledContentShareWidget
        extraProps={{
          copyLink: {
            tooltipPlacement,
            variant: "tooltip",
          },
        }}
        position="article_adhesive"
        reference={content}
        style={buttonStyle}
        types={types}
        {...$props}
      />
    </Container>
  );
};

ArticleShareWidget.displayName = "ArticleShareWidget";
